import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import API_BASE_URL from "../../config/api";
import "./certificate.css";

const events = [
  "Binary Runes",
  "Arduino Alchemy",
  "BGMI Rush",
  "Project Nexus",
  "Arcane Quiz",
  "Runic Maze",
  "Zaun Race",
  "Construct Clash",
  "Hex Track",
];

function Certificate() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({ name: "", email: "", usn: "", event: "" });
  const [feedback, setFeedback] = useState({ q1: "", q2: "", q3: "" });
  const [states, setStates] = useState({
    loading: false,
    error: "",
    success: "",
  });

  const handleInputChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleFeedbackChange = (e) =>
    setFeedback((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const proceedToDetails = (e) => {
    e.preventDefault();
    setStates(prev => ({ ...prev, error: "", success: "" }));
    
    const { q1, q2, q3 } = feedback;

    if (!q1 || !q2 || !q3.trim()) {
      setStates(prev => ({ ...prev, error: "Please answer all feedback questions." }));
      window.scrollTo(0, 0);
      return;
    }

    setStep(2);
    window.scrollTo(0, 0);
  };

  const handleSubmitAll = async (e) => {
    e.preventDefault();
    setStates(prev => ({ ...prev, error: "", success: "" }));
    
    const { name, email, usn, event } = formData;

    if (!name.trim() || !event.trim() || (!email.trim() && !usn.trim())) {
      setStates(prev => ({ ...prev, error: "Please fill Name, Event, and either Email or USN." }));
      return;
    }

    setStates(prev => ({ ...prev, loading: true }));

    try {
      // 1. Submit Feedback
      const payload = {
        name: name.trim(),
        event: event.trim(),
        answers: feedback,
      };

      if (email.trim()) {
        payload.email = email.trim().toLowerCase();
      }

      if (usn.trim()) {
        payload.usn = usn.trim().toUpperCase();
      }

      await axios.post(
        `${API_BASE_URL}/api/feedback/submit-feedback`,
        payload
      );

      // 2. Generate Certificate
      const resCert = await axios.post(
        `${API_BASE_URL}/api/certificate/generate-certificate`,
        { name: name.trim(), email: email.trim().toLowerCase(), usn: usn.trim().toUpperCase(), event },
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([resCert.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${event.replace(/\s+/g, "_")}_certificate.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setStates({ loading: false, error: "", success: "Feedback submitted and Certificate generated successfully!" });
      setStep(3); // Success Screen
      window.scrollTo(0, 0);
    } catch (err) {
      setStates(prev => ({ 
        ...prev, 
        loading: false, 
        error: err.response?.data?.message || err.response?.data?.error || "Failed to process request. Please try again." 
      }));
      window.scrollTo(0, 0);
    }
  };

  return (
    <div className="cyber-bg certificate-page">
      <div className="certificate-container">
        <h1 className="certificate-title glaze-text">ROBOCOR '26 Certificates</h1>

        {states.error && (
          <div className="cert-alert cert-alert-error">
            {states.error}
            {states.error.toLowerCase().includes("not found") && (
              <div className="cert-alert-action">
                <Link to="/claim-certificate">
                  Click here to submit a missing certificate claim.
                </Link>
              </div>
            )}
          </div>
        )}
        {states.success && <div className="cert-alert cert-alert-success">{states.success}</div>}

        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <p style={{ color: "rgba(255,255,255,0.7)" }}>
            Haven't received your certificate?{" "}
            <Link to="/claim-certificate" style={{ color: "var(--register-text)", textDecoration: "underline", fontWeight: "bold" }}>
              Submit a Claim
            </Link>
          </p>
        </div>

        {step === 1 && (
          <div className="certificate-card">
            <p className="cert-subtitle glaze-text">Step 1: Event Feedback</p>
            <form onSubmit={proceedToDetails}>
              <div className="form-section">
                <div className="feedback-box mb-4">
                  <label className="cert-label">Q1: How was your experience participating in ROBOCOR'26?</label>
                  <div className="radio-group row">
                    {["Excellent", "Good", "Average", "Bad"].map((label, index) => (
                      <label key={`q1-${index}`} className="radio-label">
                        <input type="radio" name="q1" value={label} checked={feedback.q1 === label} onChange={handleFeedbackChange} />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="feedback-box mb-4">
                  <label className="cert-label">Q2: How was the event which you participated in?</label>
                  <div className="radio-group row">
                    {["Excellent", "Good", "Average", "Bad"].map((label, index) => (
                      <label key={`q2-${index}`} className="radio-label">
                        <input type="radio" name="q2" value={label} checked={feedback.q2 === label} onChange={handleFeedbackChange} />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="feedback-box mb-4">
                  <label className="cert-label">Q3: Any suggestions or improvements you'd like to share?</label>
                  <textarea name="q3" placeholder="Type your suggestions here..." value={feedback.q3} onChange={handleFeedbackChange} className="cert-input" rows={3} />
                </div>
              </div>

              <button type="submit" className="cert-btn primary mt-3">
                Submit Feedback & Proceed
              </button>
            </form>
          </div>
        )}

        {step === 2 && (
          <div className="certificate-card">
            <p className="cert-subtitle glaze-text">Step 2: Participant Details (to generate your certificate)</p>
            <form onSubmit={handleSubmitAll}>
              <div className="form-section">
                <input type="text" name="name" placeholder="Enter your full name"
                  value={formData.name} onChange={handleInputChange} className="cert-input mb-3" />
                  
                <input type="email" name="email" placeholder="Enter your email"
                  value={formData.email} onChange={handleInputChange} className="cert-input mb-3" />
                  
                <input type="text" name="usn" placeholder="Enter your USN (optional if Email filled)"
                  value={formData.usn} onChange={handleInputChange} className="cert-input mb-3" />
                  
                <select name="event" value={formData.event} onChange={handleInputChange} className="cert-input cert-select mb-3">
                  <option value="">Select Event</option>
                  {events.map((eventName) => (
                    <option key={eventName} value={eventName}>{eventName}</option>
                  ))}
                </select>
              </div>

              <div className="button-group mt-3" style={{ display: 'flex', gap: '1rem' }}>
                <button type="button" onClick={() => setStep(1)} className="cert-btn secondary" style={{ flex: 1, backgroundColor: 'transparent' }} disabled={states.loading}>
                  Back
                </button>
                <button type="submit" className="cert-btn generate-btn" style={{ flex: 2 }} disabled={states.loading}>
                  {states.loading ? "Processing..." : "Generate Certificate"}
                </button>
              </div>
            </form>
          </div>
        )}

        {step === 3 && (
           <div className="certificate-card success-card">
              <h2 className="section-title success-text">All Done!</h2>
              <p className="cert-subtitle">Your feedback has been saved and your certificate should be downloading now.</p>
              
              <div className="summary-box mt-4 mb-4">
                <p><strong>Name:</strong> {formData.name}</p>
                <p><strong>Event:</strong> {formData.event}</p>
              </div>

              <button onClick={() => {
                setStep(1);
                setFormData({ name: "", email: "", usn: "", event: "" });
                setFeedback({ q1: "", q2: "", q3: "" });
                setStates({ loading: false, error: "", success: ""});
              }} className="cert-btn secondary">
                Generate Another
              </button>
           </div>
        )}
      </div>
    </div>
  );
}

export default Certificate;
